import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dropdown, Form, Input, Select, Tag, Tooltip, message } from 'antd';
import {
  BulbOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  FileExcelOutlined,
  FlagOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  TagOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import DataTable from '@/components/data-table';
import { ClearAllFiltersButton, ToggleFilterButton } from '@/components/data-table-actions';
import { HowToCard } from '@/components/how-to-card';
import { Sidebar } from '@/components/sidebar';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import {
  exportBillingRequests,
  getBillingRequests,
  setBillingResolved,
  setBillingStatus,
} from '@/services/teamBilling.service';
import { BillingBilledDialog, BillingPriceDialog, BillingRequestDialog } from './BillingDialogs';

const STATUSES = ['OPEN', 'CONFIRMED', 'FOR_BILLING', 'BILLED', 'CANCELLED'];
const STATUS_COLOR = { OPEN: 'blue', CONFIRMED: 'cyan', FOR_BILLING: 'gold', BILLED: 'green', CANCELLED: 'default' };
const HOW_TO_ICONS = [PlusOutlined, DollarOutlined, CheckCircleOutlined, TagOutlined, FlagOutlined];

// `billing…` keys: the board on the same page keeps its own filters in the address.
const FILTER_DEFAULTS = Object.freeze({
  billingSearch: undefined,
  billingStatus: undefined,
  billingCompany: undefined,
  billingRequest: undefined,
});

const str = (v) => (Array.isArray(v) ? v.join(',') : v === undefined || v === null ? '' : String(v));
const list = (v) => (Array.isArray(v) ? v : v ? String(v).split(',') : []);
const fmtDay = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '');
const money = (value) =>
  Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** A single line with the whole text in a tooltip, so a column never grows. */
function OneLine({ text, onClick }) {
  if (!text) return <span className="opacity-40">—</span>;
  return (
    <Tooltip title={text} placement="topLeft">
      {onClick ? (
        <button type="button" className="block w-full truncate text-left font-semibold hover:underline" onClick={onClick}>
          {text}
        </button>
      ) : (
        <div className="truncate">{text}</div>
      )}
    </Tooltip>
  );
}

/**
 * Billing requests: one-off extra charges for a client. The server pages,
 * filters and decides who sees and changes what; the export carries the same
 * scope and filters.
 *
 * @param {Object} props
 * @param {Object} props.options - what the tab needs around the list, see getBillingOptions
 */
export default function BillingView({ options }) {
  const { t } = useTranslation(['teamBilling', 'tables', 'forms']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [priceOf, setPriceOf] = useState(null);
  const [billedOf, setBilledOf] = useState(null);

  const filters = useMemo(
    () => ({
      search: str(queryParams.billingSearch) || undefined,
      status: str(queryParams.billingStatus) || undefined,
      company: str(queryParams.billingCompany) || undefined,
      id: str(queryParams.billingRequest) || undefined,
      sort: str(queryParams.billingSort) || undefined,
      order: str(queryParams.billingOrder) || undefined,
    }),
    [queryParams]
  );
  const offset = str(queryParams.offset) || '1';
  const limit = str(queryParams.limit) || '50';

  const { data, status, error } = useQuery({
    queryKey: ['team-billing', filters, offset, limit],
    queryFn: () => getBillingRequests({ ...filters, offset, limit }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const exporting = useMutation({
    mutationFn: () => exportBillingRequests(filters),
    onSuccess: (blob) => saveBlob(blob, `Billing_${new Date().toISOString().slice(0, 10)}.xlsx`),
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const done = {
    onSuccess: () => {
      message.success(t('messages.saved'));
      queryClient.invalidateQueries({ queryKey: ['team-billing'] });
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  };
  const resolve = useMutation({ mutationFn: setBillingResolved, ...done });
  const move = useMutation({ mutationFn: setBillingStatus, ...done });
  const busy = resolve.isPending || move.isPending;

  useEffect(() => {
    form.setFieldsValue({
      billingSearch: str(queryParams.billingSearch),
      billingStatus: list(queryParams.billingStatus),
      billingCompany: str(queryParams.billingCompany) || undefined,
    });
  }, [queryParams, form]);

  const resetFilters = () => {
    setQueryParams({ ...FILTER_DEFAULTS, offset: 1 });
    form.resetFields();
  };

  const applyFilters = (values) => {
    setQueryParams({
      billingSearch: values.billingSearch?.trim() || undefined,
      billingStatus: values.billingStatus?.length ? values.billingStatus.join(',') : undefined,
      billingCompany: values.billingCompany || undefined,
      billingRequest: undefined,
      offset: 1,
    });
    setFiltersOpen(false);
  };

  const nextStep = (row) => {
    if (!row.canManage) return null;
    if (row.status === 'OPEN') {
      return { key: 'priceConfirm', icon: <DollarOutlined />, onClick: () => setPriceOf(row) };
    }
    if (row.status === 'CONFIRMED' && !row.resolved) {
      return {
        key: 'resolve',
        icon: <CheckCircleOutlined />,
        onClick: () => resolve.mutate({ _id: row._id, resolved: true }),
      };
    }
    if (row.status === 'FOR_BILLING') {
      return { key: 'billed', icon: <TagOutlined />, onClick: () => setBilledOf(row) };
    }
    return null;
  };

  const moreItems = (row) => {
    const go = (status) => () => move.mutate({ _id: row._id, status });
    const items = [];
    if (row.canEdit) items.push({ key: 'edit', onClick: () => setDialog({ request: row }) });
    if (row.canManage && row.status === 'BILLED') items.push({ key: 'invoiceNote', onClick: () => setBilledOf(row) });
    if (row.canManage && ['CONFIRMED', 'FOR_BILLING'].includes(row.status)) {
      items.push({ key: 'changePrice', onClick: () => setPriceOf(row) });
    }
    if (row.canManage && row.status === 'OPEN' && !row.resolved) {
      items.push({ key: 'resolve', onClick: () => resolve.mutate({ _id: row._id, resolved: true }) });
    }
    if (row.canManage && ['OPEN', 'CONFIRMED'].includes(row.status) && row.resolved) {
      items.push({ key: 'unresolve', onClick: () => resolve.mutate({ _id: row._id, resolved: false }) });
    }
    const moves = {
      FOR_BILLING: row.status === 'BILLED' ? 'backToForBilling' : 'moveNow',
      OPEN: row.status === 'CANCELLED' ? 'reopen' : 'backToOpen',
      CONFIRMED: 'backToConfirmed',
      CANCELLED: 'cancel',
    };
    (row.moves || []).forEach((target) => {
      if (target === 'BILLED') return;
      // Billing before the work is confirmed done is refused anyway; do not offer it.
      if (target === 'FOR_BILLING' && row.status === 'CONFIRMED' && !row.resolved) return;
      items.push({ key: moves[target], danger: target === 'CANCELLED', onClick: go(target) });
    });
    return items.map((item) => ({
      ...item,
      label: item.key === 'invoiceNote' ? t('form.invoiceNote') : t(`actions.${item.key}`),
    }));
  };

  const sorter = (field) => ({
    sorter: true,
    showSorterTooltip: false,
    sortOrder:
      (filters.sort || 'createdAt') === field ? (filters.order === 'asc' ? 'ascend' : 'descend') : null,
  });

  const columns = [
    {
      title: t('columns.title'),
      dataIndex: 'title',
      key: 'title',
      width: '24%',
      ...sorter('title'),
      render: (title, row) => <OneLine text={title} onClick={() => setDialog({ request: row })} />,
    },
    {
      title: t('columns.client'),
      dataIndex: 'client',
      key: 'client',
      width: '17%',
      render: (client) => <OneLine text={client} />,
    },
    {
      title: t('columns.lead'),
      dataIndex: 'leadName',
      key: 'lead',
      width: '12%',
      render: (name) => <OneLine text={name} />,
    },
    {
      title: t('columns.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 104,
      ...sorter('dueDate'),
      render: (d) => (d ? fmtDay(d) : <span className="opacity-40">—</span>),
    },
    {
      title: t('columns.price'),
      dataIndex: 'price',
      key: 'price',
      width: 136,
      align: 'right',
      ...sorter('price'),
      render: (price, row) =>
        price === null ? (
          <span className="opacity-40">{t('noPrice')}</span>
        ) : (
          <span className="whitespace-nowrap">
            {money(price)} {row.currency}
          </span>
        ),
    },
    {
      title: t('columns.status'),
      key: 'status',
      width: 158,
      render: (_, row) => (
        <div className="grid justify-items-start gap-1">
          <Tag color={STATUS_COLOR[row.status]} className="m-0">
            {t(`status.${row.status}`)}
          </Tag>
          {['OPEN', 'CONFIRMED'].includes(row.status) && row.resolved && (
            <Tag color="green" className="m-0">
              {t('resolvedTag')}
            </Tag>
          )}
          {/* While open nothing waits for it yet: the price dialog settles it in one go. */}
          {row.status === 'CONFIRMED' && !row.resolved && (
            <Tag color="orange" className="m-0">
              {t('notResolvedTag')}
            </Tag>
          )}
          {row.status === 'CONFIRMED' && row.resolved && row.billingDay && options.remindersEnabled && (
            <span className="text-xs opacity-70">{t('billingDay', { date: fmtDay(row.billingDay) })}</span>
          )}
        </div>
      ),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      width: 216,
      align: 'right',
      render: (_, row) => {
        const step = nextStep(row);
        const items = moreItems(row);
        return (
          <div className="flex items-center justify-end gap-1">
            {step && (
              <Button size="small" shape="round" type="primary" icon={step.icon} disabled={busy} onClick={step.onClick}>
                {t(`actions.${step.key}`)}
              </Button>
            )}
            {items.length > 0 && (
              <Dropdown trigger={['click']} placement="bottomRight" menu={{ items }}>
                <Button size="small" shape="circle" icon={<MoreOutlined />} aria-label={t('actions.more')} disabled={busy} />
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
      <HowToCard
        title={t('howTo.title')}
        sections={[
          {
            title: t('howTo.section'),
            steps: t('howTo.steps', { returnObjects: true }).map((step, i) => ({
              ...step,
              icon: HOW_TO_ICONS[i] || BulbOutlined,
            })),
          },
        ]}
      />

      <Alert
        type={options.remindersEnabled ? 'info' : 'warning'}
        showIcon
        message={
          options.remindersEnabled
            ? t('remindersOn', {
                forBilling: fmtDay(options.schedule?.forBilling),
                monthEnd: fmtDay(options.schedule?.monthEnd),
              })
            : t('remindersOff')
        }
      />

      <DataTable
        rowKey="_id"
        title={`${t('title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={status === 'pending' ? 'pending' : error ? 'error' : 'success'}
        columns={columns}
        offset={offset}
        limit={limit}
        onChangePagination={() => {}}
        onTableChange={({ sorter: s }) => {
          if (!s || Array.isArray(s)) return;
          setQueryParams({
            billingSort: s.order ? s.columnKey : undefined,
            billingOrder: s.order === 'ascend' ? 'asc' : undefined,
            offset: 1,
          });
        }}
        tableLayout="fixed"
        size="small"
        locale={error ? { emptyText: error?.response?.data?.message || error.message } : undefined}
        headerChildren={
          <>
            {options.canOpen && (
              <Button className="btn-default h-8 text-black" shape="round" icon={<PlusOutlined />} onClick={() => setDialog({ request: null })}>
                {t('new')}
              </Button>
            )}
            <Button
              className="btn-default h-8 text-black"
              shape="round"
              icon={<FileExcelOutlined />}
              loading={exporting.isPending}
              onClick={() => exporting.mutate()}
            >
              {t('export')}
            </Button>
            <ToggleFilterButton label={t('filters', { ns: 'tables' })} onClick={() => setFiltersOpen(true)} defaultParams={FILTER_DEFAULTS} />
            <ClearAllFiltersButton label={t('clearAllFilters', { ns: 'tables' })} onReset={resetFilters} defaultParams={FILTER_DEFAULTS} />
          </>
        }
      />

      <Sidebar title={t('filters', { ns: 'tables' })} open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <Form form={form} layout="vertical" className="form p-4" onFinish={applyFilters}>
          <Form.Item name="billingSearch" label={t('filter.search')}>
            <Input allowClear prefix={<SearchOutlined />} placeholder={t('filter.searchPlaceholder')} />
          </Form.Item>
          <Form.Item name="billingStatus" label={t('filter.status')}>
            <Select
              mode="multiple"
              allowClear
              placeholder={t('filter.any')}
              options={STATUSES.map((value) => ({ value, label: t(`status.${value}`) }))}
            />
          </Form.Item>
          <Form.Item name="billingCompany" label={t('filter.company')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('filter.any')}
              options={options.companies.map((c) => ({ value: c._id, label: c.name }))}
            />
          </Form.Item>
          <div className="form-footer form-footer--sticky">
            <div className="flex gap-2">
              <Button type="default" shape="round" className="btn-default btn-default--hover" onClick={resetFilters}>
                {t('reset', { ns: 'forms' })}
              </Button>
              <Button type="primary" shape="round" htmlType="submit" className="btn-primary">
                {t('submit', { ns: 'forms' })}
              </Button>
            </div>
          </div>
        </Form>
      </Sidebar>

      <BillingRequestDialog
        open={!!dialog}
        request={dialog?.request || null}
        options={options}
        onClose={() => setDialog(null)}
      />
      <BillingPriceDialog request={priceOf} options={options} onClose={() => setPriceOf(null)} />
      <BillingBilledDialog request={billedOf} onClose={() => setBilledOf(null)} />
    </div>
  );
}
